import { TIMEOUT_STRING, AMAZON_SUFFIX, DarkModeValue } from "../../utils/constants";
import { retryWithBackoff, isRetryableStatus, MAX_RETRIES } from "../../utils/retryUtils";

// Type definitions
interface PostMessagePromise {
  promise: Promise<any>;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}

interface BaseMessage {
  method: string;
  args: Record<string, any>;
  guid?: string;
  url: string; 
}

interface MessageEventData {
  method: string;
  guid?: string;
  responseData?: any;
  args?: Record<string, any>;
}

interface ApiGatewayResponseData {
  guid: string;
  responseData: any;
}

interface TCPResponseData {
  guid: string;
  childData: any;
}

interface MetricRequest {
  processName: string;
  metricName: string;
  metricValue: number;
  metricType: string;
}

// Global state
const postMessagePromises: Record<string, PostMessagePromise> = {};

function generateGuid(): string {
  /**
   * @return {string}
   */
  function S4(): string {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  }
  return (
    S4() +
    S4() +
    "-" +
    S4() +
    "-4" +
    S4().substr(0, 3) +
    "-" +
    S4() +
    "-" +
    S4() +
    S4() +
    S4()
  ).toLowerCase();
}

async function postMessagePromised(
  targetWindow: Window,
  message: BaseMessage,
  targetOrigin: string,
  timeout?: number,
  maxRetries: number = MAX_RETRIES
): Promise<any> {
  return retryWithBackoff(
    async () => {
      // Generate NEW GUID for each attempt
      const guid = generateGuid();
      message.guid = guid;
      
      postMessagePromises[guid] = {} as PostMessagePromise;
      return new Promise<any>((resolve, reject) => {
        // Cleanup GUID after completion to prevent memory leaks
        // Critical with retries since each retry generates a new GUID
        const cleanupResolve = (value: any) => {
          delete postMessagePromises[guid];
          resolve(value);
        };
        const cleanupReject = (reason?: any) => {
          delete postMessagePromises[guid];
          reject(reason);
        };
        
        postMessagePromises[guid]["resolve"] = cleanupResolve;
        postMessagePromises[guid]["reject"] = cleanupReject;
        
        targetWindow.postMessage(message, targetOrigin);
        
        if (timeout) {
          setTimeout(() => {
            cleanupReject(new Error(TIMEOUT_STRING));
          }, timeout);
        }
      });
    },
    (response) => {
      // Retry on timeout or retryable errors (5xx)
      // Don't retry client errors (4xx) or ValidationFailedException
      const isTimeout = response instanceof Error && response.message === TIMEOUT_STRING;
      return isTimeout || isRetryableStatus(response);
    },
    maxRetries
  );
}

const API_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export async function getAPIGatewayDataFromParent(
  args: Record<string, any>
): Promise<any> {
  try {
    return await postMessagePromised(
      window.parent,
      {
        method: "postGenericFromParent",
        args: args,
        url: "/proxyapigateway/relo/data",
      },
      "*",
      API_TIMEOUT_MS
    );
  } catch (error) {
    console.error('[BosonClient] getAPIGatewayDataFromParent failed:', error);
    throw error;
  }
}

window.addEventListener("message", onNewPostMessageFromParent, false);

function onNewPostMessageFromParent(event: MessageEvent<MessageEventData>) {
  if (
    event.data.method &&
    subscribeAbleEvents.hasOwnProperty(event.data.method)
  ) {
    subscribeAbleEvents[event.data.method](event.data);
  }
}

export async function getTcpRoles() {
  return await postMessagePromised(
    window.parent,
    {
      method: "getTcpRoles",
      args: {},
      url: ""
    },
    "*",
    200
  );
}

export async function getUserLoginFromParent() {
  return await postMessagePromised(
    window.parent,
    {
      method: "getTcpEmployeeLogin",
      args: {},
      url: ""
    },
    "*",
    200
  );
}

export async function getUserNameFromParent() {
  return await postMessagePromised(
    window.parent,
    {
      method: "getEmployeeName",
      args: {},
      url: ""
    },
    "*",
    200
  );
}

// Dark Mode functions
let setCurrentDarkMode: (darkMode: DarkModeValue) => void = () => {};

export function setDarkModeStateListener(darkModeStateListener: (darkMode: DarkModeValue) => void) {
  setCurrentDarkMode = darkModeStateListener;
}

export async function getDarkModeStatus() {
  return await postMessagePromised(
    window.parent,
    {
      method: "getDarkModeStatus",
      args: {},
      url: ""
    },
    "*"
  );
}

const subscribeAbleEvents: Record<string, (data: any) => void> = {
  sendApiGatewayResponse: sendApiGatewayResponse,
  postGenericFromParentResponse: postGenericFromParentResponse,
  sendTcpRolesResponse: sendTcpRolesResponse,
  sendEmployeeNameResponse: sendEmployeeNameResponse,
  sendTcpEmployeeLoginResponse: sendTcpEmployeeLoginResponse,
  sendDarkModeStatus: sendDarkModeStatus,
};

export async function getUserName(): Promise<string> {
  let userName = await getTcpEmployee();
  if (!userName.endsWith(AMAZON_SUFFIX)) {
    userName += AMAZON_SUFFIX;
  }
  return userName;
}

export async function getTcpEmployee() {
  let response = await getUserLoginFromParent();
  if (response === TIMEOUT_STRING) {
    response = await getUserNameFromParent();
  }
  if (response.name === undefined) {
    return "";
  }
  return JSON.parse(response.name);
}

function sendApiGatewayResponse(data: ApiGatewayResponseData) {
  if (postMessagePromises[data.guid]) {
    const responseData = data.responseData;
    if (responseData && responseData.status && responseData.status >= 400) {
      postMessagePromises[data.guid].reject(responseData);
    } else {
      postMessagePromises[data.guid].resolve(responseData);
    }
  }
}

function postGenericFromParentResponse(data: ApiGatewayResponseData) {
  if (postMessagePromises[data.guid]) {
    const responseData = data.responseData;
    if (responseData && responseData.status && responseData.status >= 400) {
      postMessagePromises[data.guid].reject(responseData);
    } else {
      postMessagePromises[data.guid].resolve(responseData);
    }
  }
}

function sendTcpEmployeeLoginResponse(data: TCPResponseData) {
  if (postMessagePromises[data.guid]) {
    postMessagePromises[data.guid].resolve(data.childData);
  }
}

function sendEmployeeNameResponse(data: TCPResponseData) {
  if (postMessagePromises[data.guid]) {
    postMessagePromises[data.guid].resolve(data.childData);
  }
}

function sendTcpRolesResponse(data: TCPResponseData) {
  if (postMessagePromises[data.guid]) {
    postMessagePromises[data.guid].resolve(data.childData);
  }
}

export function publishClientSideMetrics(args: MetricRequest) {
  window.parent.postMessage(
    {
      method: "publishClientSideMetrics",
      args: args,
    },
    "*"
  );
}

function sendDarkModeStatus(data: any) {
  // Parent sends dark mode via data.childData.capability (see ShiftPlanManagementWebsite parentDataHandler.js)
  // The value is the raw capability string like "DARK" or "LIGHT"
  if (data.childData && data.childData.capability !== undefined) {
    const capability: string = data.childData.capability;
    const darkMode: DarkModeValue = capability.toLocaleUpperCase() === 'DARK' ? 'DARK' : 'LIGHT';
    console.log(`[BosonClient] Dark mode status from parent: capability=${capability}, mapped=${darkMode}`);
    setCurrentDarkMode(darkMode);
  } else if (data.args && data.args.darkMode !== undefined) {
    // Fallback for alternative message format
    const darkMode: DarkModeValue = data.args.darkMode === 'dark' ? 'DARK' : 'LIGHT';
    console.log(`[BosonClient] Dark mode status from parent (fallback): ${data.args.darkMode}, mapped=${darkMode}`);
    setCurrentDarkMode(darkMode);
  }
  
  // Resolve the getDarkModeStatus promise if this is a response to our request
  if (data.guid && postMessagePromises[data.guid]) {
    postMessagePromises[data.guid].resolve(data);
  }
}